/**
 * Store-backed export + share controller — image capture and share-by-link
 * over the WorkspaceStore. Files, selection and the observed viewport come
 * from the store; the live capture handle is the one inherently board-bound
 * input (the viewer passes its `InfiniteBoardHandle` ref, the workbench
 * passes BoardPane's mirrored `ArchitectureViewHandle`). Doc assembly,
 * filenames and the clipboard flow live in `exportShareCore` — this hook
 * only adds React state (the blocking-export flag) and stable handlers.
 *
 * Shared by the viewer's SourceMenu chrome and the workbench's
 * ExportShareMenu primitive, so the two surfaces cannot drift.
 */
import { useCallback, useState, type RefObject } from "react";
import type { Vfs } from "@archlang/viewer-core";
import {
  buildWorkspaceShareDoc,
  copyShareLinkToClipboard,
  focalBasename,
  type BoardCaptureHandle,
} from "../exportShareCore.js";
import type { ShareDoc, ShareOutcome } from "../shareLink.js";
import type { WorkspaceStore } from "./WorkspaceStore.js";

export interface ExportShareActionsInput {
  /** Live board capture handle. Captures reject while it's null. */
  readonly capture: RefObject<BoardCaptureHandle | null>;
  /** Workspace label — share-link label + export filename fallback. */
  readonly label: string;
  /** `indexes?.moduleQualifiedNameById` — names the export by the focal. */
  readonly qualifiedNameById?: ReadonlyMap<string, string>;
  /** The other side of an active diff (viewer compare mode), or null. */
  readonly compare?: {
    readonly vfs: Vfs;
    readonly label: string;
    readonly swapped: boolean;
  } | null;
}

export interface ExportShareController {
  readonly exportingPng: boolean;
  readonly handleExportPng: () => Promise<void>;
  readonly handleExportSvg: () => Promise<void>;
  readonly handleCopyImage: () => Promise<void>;
  readonly handleShareWorkspace: () => Promise<ShareOutcome>;
  readonly handleShareView: () => Promise<ShareOutcome>;
  /** Share the workspace as a link whose pathname is `path` — an alternate
   *  surface's route, so the link reopens in that surface's chrome. */
  readonly handleShareAt: (path: string) => Promise<ShareOutcome>;
}

export function useExportShareActions(
  store: WorkspaceStore,
  input: ExportShareActionsInput,
): ExportShareController {
  const { capture, label, qualifiedNameById, compare } = input;
  const [exportingPng, setExportingPng] = useState(false);

  const basename = useCallback(
    () =>
      focalBasename(
        capture.current?.getViewport() ?? store.getLastViewport(),
        qualifiedNameById,
        label,
      ),
    [capture, store, qualifiedNameById, label],
  );

  // Shared runner for the heavy, blocking capture exports. Flips the
  // "exporting" overlay on, lets it paint, runs the capture, and reports
  // failures to the caller (which surfaces them in its menu).
  const runExport = useCallback(
    async (fn: (handle: BoardCaptureHandle) => Promise<void>) => {
      const handle = capture.current;
      if (!handle) throw new Error("Board isn't ready yet");
      setExportingPng(true);
      // Let the overlay paint before kicking off the capture — otherwise
      // the user sees nothing during the lag.
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      try {
        await fn(handle);
      } finally {
        setExportingPng(false);
      }
    },
    [capture],
  );

  const handleExportPng = useCallback(
    () => runExport((h) => h.exportFocalAsPng({ filename: `${basename()}.png` })),
    [runExport, basename],
  );
  const handleExportSvg = useCallback(
    () => runExport((h) => h.exportFocalAsSvg({ filename: `${basename()}.svg` })),
    [runExport, basename],
  );
  const handleCopyImage = useCallback(() => {
    const handle = capture.current;
    if (!handle) return Promise.reject(new Error("Board isn't ready yet"));
    // NOT runExport: its paint-the-overlay rAF awaits before the action,
    // and Safari voids the user gesture at the first await — the clipboard
    // write must start synchronously in the click. The board handle calls
    // clipboard.write immediately with a Promise<Blob>; the overlay still
    // gets its paint frame from the capture's own deferred start.
    setExportingPng(true);
    return handle.copyFocalToClipboard().finally(() => setExportingPng(false));
  }, [capture]);

  // Serialize the current workspace (+ active diff, + optionally the
  // current view) into a self-contained `#s=` link. Files + selection are
  // read from the store AT CALL TIME, so the handlers stay stable across
  // keystrokes without going stale.
  const buildShareDoc = useCallback(
    (includeView: boolean): ShareDoc => {
      const state = store.getState();
      const vp = includeView
        ? capture.current?.getViewport() ?? store.getLastViewport()
        : null;
      return buildWorkspaceShareDoc({
        vfs: state.files,
        assets: state.assets,
        label,
        view:
          includeView && vp
            ? {
                viewport: vp,
                selectedModuleId: state.selection.moduleId,
                selectedEdgeId: state.selection.edgeId,
                selectedInterfaceKey: state.selection.interfaceKey,
              }
            : undefined,
        compare: compare ?? null,
      });
    },
    [store, capture, label, compare],
  );

  const copyShareLink = useCallback(
    (includeView: boolean): Promise<ShareOutcome> =>
      copyShareLinkToClipboard(buildShareDoc(includeView)),
    [buildShareDoc],
  );

  const handleShareWorkspace = useCallback(() => copyShareLink(false), [copyShareLink]);
  const handleShareView = useCallback(() => copyShareLink(true), [copyShareLink]);

  // Route-scoped share: same payload, but the link points at `path` so it
  // reopens in that surface's chrome (the scratch surface shares `/scratch`).
  const handleShareAt = useCallback(
    (path: string) => copyShareLinkToClipboard(buildShareDoc(false), { path }),
    [buildShareDoc],
  );

  return {
    exportingPng,
    handleExportPng,
    handleExportSvg,
    handleCopyImage,
    handleShareWorkspace,
    handleShareView,
    handleShareAt,
  };
}
